import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import { logout, resetAllState } from "../../../redux/Reducer/authSlice";
import Button from "../../common/Button";
import { persistor } from "../../../redux/store";
import {
  updateStatus,
} from "../../../redux/Thunk/Doctor/DoctorThunks";
import CheckedBg from "../../common/icons/CheckedBg";
import Close from "../../common/icons/Close";
import Timing from "../../common/icons/Timing";
import StudioIcon from "../../common/icons/StudioIcon";
import Logout from "../../common/icons/Logout";
import TrackingArrow from "../../common/icons/TrackingArrow";
import HubIcon from "../../common/icons/HubIcon";
import PlannerIcon from "../../common/icons/PlannerIcon";
import HealthMapIcon from "../../common/icons/HealthMapIcon";
import UserAccount from "../../common/icons/UserAccount";
import ArrowBack from "../../common/icons/ArrowBack";
import apiAxios from "../../../services/apiAxios";
import toast from "react-hot-toast";
import { setSidebarVisible } from "../../../redux/Reducer/Doctor/DoctorSlice";

const Sidebar = () => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const [selectedRoute, setSelectedRoute] = useState("");
  const [isConfirm, setIsConfirm] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("");
  const doctorDetails = useSelector(
    (state) => state?.doctor?.doctorDetails?.doctor
  );
  const isView = useSelector((state) => state?.auth?.user?.isPinB);
  const { role, user } = useSelector((state) => state.auth);

  const isSidebarVisible = useSelector((state) => state.doctor?.isSidebarVisible);

  // Auto-hide Logic
  useEffect(() => {
    let timeoutId;
    const resetTimer = () => {
      if (timeoutId) clearTimeout(timeoutId);
      // Wait 10 seconds of inactivity before hiding
      timeoutId = setTimeout(() => {
        // Only auto-hide if on common doctor pages and not interacting
        const doctorPaths = ["/healthmap", "/studio", "/tracking", "/hub", "/planner", "/account"];
        if (doctorPaths.includes(pathname)) {
          dispatch(setSidebarVisible(false));
        }
      }, 5000); // define time in ms
    };

    const doctorPaths = ["/healthmap", "/studio", "/tracking", "/hub", "/planner", "/account"];
    if (isSidebarVisible && doctorPaths.includes(pathname)) {
      resetTimer();
      // Reset timer on any mouse movement or click within the sidebar
      const sidebarElement = document.getElementById("doctor-sidebar");
      if (sidebarElement) {
        sidebarElement.addEventListener("mousemove", resetTimer);
        sidebarElement.addEventListener("click", resetTimer);
      }

      return () => {
        if (timeoutId) clearTimeout(timeoutId);
        if (sidebarElement) {
          sidebarElement.removeEventListener("mousemove", resetTimer);
          sidebarElement.removeEventListener("click", resetTimer);
        }
      };
    }
  }, [isSidebarVisible, pathname, dispatch]);
  useEffect(() => {
    if (!user?.token || role !== "doctor") {
      dispatch(resetAllState());
      localStorage.clear();
      persistor.purge();
      window.location.href = process.env.REACT_APP_DOCTOR_URL;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = async () => {
    if (isConfirm) {
      const loadingToast = toast.loading("Logging out of all sessions...");
      try {
        // Global Logout: Close all sessions found in localStorage
        const loggedInDoctors = JSON.parse(localStorage.getItem("loggedInDoctors") || "[]");
        
        const logoutPromises = loggedInDoctors.map(acc => 
          apiAxios.patch(
            `api/doctor/update-doctor-status`,
            { currentDoctorStatus: "CLOSED" },
            { headers: { Authorization: `Bearer ${acc.token}` } }
          ).catch(err => {
            console.error(`Logout failed for DID: ${acc.DID}`, err);
            return null; // Continue even if one fails
          })
        );

        await Promise.all(logoutPromises);
        
        // Finalize logout locally
        dispatch(logout());
        dispatch(resetAllState());
        localStorage.clear();
        persistor.purge();
        toast.dismiss(loadingToast);
        toast.success("Logged out successfully from all workspaces");
        window.location.href = process.env.REACT_APP_DOCTOR_URL;
      } catch (err) {
        toast.dismiss(loadingToast);
        toast.error("Logout failed. Please try again.");
      }
    } else {
      setIsConfirm(true);
      // Automatically reset confirmation after 5 seconds if not clicked
      setTimeout(() => setIsConfirm(false), 5000);
    }
  };

  useEffect(() => {
    setSelectedRoute(pathname);
  }, [pathname]);

  useEffect(() => {
    if (doctorDetails?.currentDoctorStatus) {
      setSelectedStatus(doctorDetails.currentDoctorStatus);
    }
  }, [doctorDetails]);

  // Handle toggling status
  const handleToggleStatus = () => {
    const STATUS_ORDER = ["ACTIVE", "AWAY", "CLOSED"];
    const currentIndex = STATUS_ORDER.indexOf(selectedStatus);
    const nextStatus = STATUS_ORDER[currentIndex >= 0 ? (currentIndex + 1) % STATUS_ORDER.length : 0];
    setSelectedStatus(nextStatus);
    const newStatus = { currentDoctorStatus: nextStatus };
    dispatch(updateStatus(newStatus));
  };


  return (
    <>
      {/* Mobile Backdrop */}
      <div 
        className={`fixed inset-0 bg-black/40 backdrop-blur-[2px] z-[998] transition-opacity duration-500 md:hidden ${isSidebarVisible ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        onClick={() => dispatch(setSidebarVisible(false))}
      />
      <div
        id="doctor-sidebar"
        className={`fixed left-0 top-0 h-[100vh] w-[80px] z-[999] flex flex-col justify-between transition-all duration-500 ease-in-out ${isSidebarVisible ? "translate-x-0" : "-translate-x-full"
          } ${isConfirm ? " bg-primary-700" : "bg-primary-600 shadow-2xl"}`}
      >
        {/* Top Section */}
        <div className="flex flex-col">
          {!isConfirm && (
            <ul className="flex flex-col">
              {isView && (
                <li>
                  <Link
                    to={"/healthmap"}
                    className={`flex flex-col items-center justify-center text-[12px] text-primary-50 gap-1 px-3 py-3 min-h-[64px] w-full transition-all duration-300 ${selectedRoute === "/healthmap"
                      ? "bg-primary-700 hover:text-primary-50/100"
                      : "hover:text-primary-50/30"
                      } font-medium`}
                  >
                    <HealthMapIcon />
                    HealthMap
                  </Link>
                </li>
              )}
              <li>
                <Link
                  to={"/studio"}
                  className={`flex flex-col items-center justify-center text-[12px] text-primary-50 gap-1 px-3 py-3 min-h-[64px] w-full transition-all duration-300 ${selectedRoute === "/studio"
                    ? "bg-primary-700 hover:text-primary-50/100"
                    : "hover:text-primary-50/30"
                    } font-medium`}
                >
                  <StudioIcon />
                  Studio
                </Link>
              </li>
              <li>
                <Link
                  to={"/planner"}
                  className={`flex flex-col items-center justify-center text-[12px] text-primary-50 gap-1 px-3 py-3 min-h-[64px] w-full transition-all duration-300 ${selectedRoute === "/planner"
                    ? "bg-primary-700 hover:text-primary-50/100"
                    : "hover:text-primary-50/30"
                    } font-medium`}
                >
                  <PlannerIcon />
                  Planner
                </Link>
              </li>
              <li>
                <Link
                  to={"/tracking"}
                  className={`flex flex-col items-center justify-center text-[12px] text-primary-50 gap-1 px-3 py-3 min-h-[64px] w-full transition-all duration-300 ${selectedRoute === "/tracking"
                    ? "bg-primary-700 hover:text-primary-50/100"
                    : "hover:text-primary-50/30"
                    } font-medium`}
                >
                  <TrackingArrow />
                  Tracking
                </Link>
              </li>
              <li>
                <Link
                  to={"/hub"}
                  className={`flex flex-col items-center justify-center text-[12px] text-primary-50 gap-1 px-3 py-3 min-h-[64px] w-full transition-all duration-300 ${selectedRoute === "/hub"
                    ? "bg-primary-700 hover:text-primary-50/100"
                    : "hover:text-primary-50/30"
                    } font-medium`}
                >
                  <HubIcon />
                  Hub
                </Link>
              </li>
            </ul>
          )}
        </div>

        {/* Bottom Section */}
        <div className="flex flex-col pb-2">
          <div className="px-2 pb-2">
            <button
            className="w-full flex flex-col items-center justify-center gap-1 text-[11px] font-medium text-primary-50 border border-white/80 rounded-md py-2 px-1 hover:bg-primary-700/50 active:scale-95 transition-all duration-200 cursor-pointer select-none"
            type="button"
            onClick={handleToggleStatus}
            title="Click to toggle status (Active -> Away -> Closed)"
          >
            {selectedStatus === "AWAY" ? (
              <Timing fill="#fff" />
            ) : selectedStatus === "ACTIVE" ? (
              <CheckedBg fill="#fff" />
            ) : selectedStatus === "CLOSED" ? (
              <Close fill="#fff" />
            ) : (
              <Close fill="#fff" />
            )}
            <span className="leading-tight">{selectedStatus || "CLOSED"}</span>
          </button>
          </div>

          <ul className="flex flex-col">
            {!isConfirm && isView && (
              <li>
                <Link
                  to={"/account"}
                  className={`flex flex-col items-center justify-center text-[12px] text-primary-50 gap-1 px-3 py-3 min-h-[64px] w-full transition-all duration-300 ${selectedRoute === "/account"
                    ? "bg-primary-700 hover:text-primary-50/100"
                    : "hover:text-primary-50/30"
                    } font-medium`}
                >
                  <UserAccount />
                  Account
                </Link>
              </li>
            )}
            {isConfirm && (
              <li>
                <Button
                  className={`flex flex-col items-center justify-center text-[12px] text-primary-50 gap-1 px-3 py-3 min-h-[64px] w-full transition-all duration-300 hover:text-primary-50/30 font-medium group`}
                  onClick={() => setIsConfirm(false)}
                >
                  <span className="transition-all duration-200 group-hover:opacity-20 scale-110">
                    <ArrowBack fill="#fff" height={25} width={25} />
                  </span>
                  Back
                </Button>
              </li>
            )}
            <li>
              <Button
                onClick={handleLogout}
                className={`flex flex-col items-center justify-center text-[12px] text-primary-50 gap-1 px-3 py-3 min-h-[64px] w-full transition-all duration-300 hover:text-primary-50/30 group ${isConfirm ? 'bg-red-500/10' : ''}`}
              >
                <span className={`transition-all duration-200 ${isConfirm ? "group-hover:opacity-20" : "group-hover:opacity-30"} scale-110`}>
                  <Logout height={23} width={23} />
                </span>
                {isConfirm ? "Logout" : "Logout"}
              </Button>
            </li>
          </ul>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
